import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import {
  CSS2DObject,
  CSS2DRenderer,
} from "three/examples/jsm/renderers/CSS2DRenderer.js";
import {
  PREVIEW_SCENE_ROAD_HALF_WIDTH_M,
  PREVIEW_SCENE_ROAD_WIDTH_M,
  PREVIEW_SCENE_VEHICLE_CHASSIS_DEPTH_M,
  PREVIEW_SCENE_VEHICLE_DISPLAY_SCALE,
  PREVIEW_SCENE_VEHICLE_GROUP_POSITION_Z_M,
  PREVIEW_SCENE_VEHICLE_TARGET_DEPTH_M,
} from "@/constants/previewSceneDisplay";
/** 几何来自 `自车x01pro/01pro.glb`；车漆贴图为外部 `01自车night/*.bmp`（与 Max UV 配套） */
import previewVehicleGlbUrl from "@/assets/3d_modles/x01pro-ego.glb?url";
/** 银色 night 车漆，与 `x01pro-night/银色.bmp` 同内容 */
import x01NightBodyBmpUrl from "@/assets/3d_modles/x01pro-night/body-night-silver.bmp?url";
import type {
  ObstacleInfo,
  ObstacleType,
  PreviewObstacleLane,
} from "./vehiclePerceptionTypes";

const FOG_COLOR = "#0d1020";

const ROAD_LENGTH = 200;
const ROAD_HALF = ROAD_LENGTH / 2;
const GRID_SPACING = 5;
const GRID_COUNT = 40;
const Z_WRAP = ROAD_LENGTH;

/** 3D 标签：此相机距离（m）时内层 scale≈1，近大远小 */
const OBSTACLE_LABEL_DEPTH_REF_DIST_M = 13;
const OBSTACLE_LABEL_SCALE_MIN = 0.38;
const OBSTACLE_LABEL_SCALE_MAX = 2.35;
/** 标签局部 Y：基础高度 + 随相机变远略抬高，减轻遮挡 */
const OBSTACLE_LABEL_BASE_Y = 1.72;
const OBSTACLE_LABEL_Y_BOOST_START_CAM_DIST_M = 9;
const OBSTACLE_LABEL_Y_BOOST_PER_M = 0.048;
const OBSTACLE_LABEL_Y_BOOST_MAX = 1.05;

const _vpLabelWorld = new THREE.Vector3();
const _vpCamWorld = new THREE.Vector3();

/** 与设置中路面预瞄量程一致（米），用于雾效 */
const PREVIEW_RANGE_MIN_M = 5;
const PREVIEW_RANGE_MAX_M = 30;

function clampPreviewRangeM (m: number): number {
  return Math.min(PREVIEW_RANGE_MAX_M, Math.max(PREVIEW_RANGE_MIN_M, m));
}

/** 量程（米）→ 与 OrbitControls 距离一致的标称机位距离（仍受 min/maxDistance 钳位） */
function orbitDistanceForRangeM (rangeM: number): number {
  const r = clampPreviewRangeM(rangeM);
  const t = (r - PREVIEW_RANGE_MIN_M) / (PREVIEW_RANGE_MAX_M - PREVIEW_RANGE_MIN_M);
  const distMin = 8.5;
  const distMax = 24;
  return distMin + t * (distMax - distMin);
}

export interface CreateVehiclePerceptionSceneOptions {
  container: HTMLElement;
  /** 车速 km/h，用于道路滚动与轮速 */
  getVehicleSpeedKmh: () => number;
  /** 由 CarProperty 驱动的障碍物（仅展示，不在此积分位移） */
  getObstacles: () => ObstacleInfo[];
  /** 前向展示量程（米），驱动雾效与标称轨道距离（可与 OrbitControls 手调叠加） */
  getPreviewDisplayRangeM: () => number;
}

/**
 * 障碍物 3D 标签 DOM：外层由 CSS2DRenderer 只写 translate，内层负责 scale（景深）
 */
function createObstacleLabelElement (
  type: ObstacleType,
  distanceM: number
): HTMLDivElement {
  const outer = document.createElement("div");
  outer.className = "vp-obs-label-root";
  outer.style.display = "inline-block";
  outer.style.lineHeight = "0";
  const inner = document.createElement("div");
  inner.setAttribute("data-vp-obs-label-inner", "1");
  inner.className =
    type === "speedBump"
      ? "vp-obs-label flex flex-row items-center gap-2 px-3 py-2 bg-vp-secondary/95 border-2 border-vp-chart-4 rounded-lg shadow-lg whitespace-nowrap"
      : "vp-obs-label flex flex-row items-center gap-2 px-3 py-2 bg-vp-secondary/95 border-2 border-vp-accent rounded-lg shadow-lg whitespace-nowrap";
  const name = type === "speedBump" ? "减速带" : "井盖";
  const title = document.createElement("span");
  title.className =
    type === "speedBump"
      ? "text-lg font-bold font-mono text-vp-chart-4/90 shrink-0"
      : "text-lg font-bold font-mono text-vp-accent/90 shrink-0";
  title.textContent = name;
  const distEl = document.createElement("span");
  distEl.setAttribute("data-vp-obs-dist", "1");
  distEl.className =
    type === "speedBump"
      ? "text-2xl font-black font-mono tabular-nums tracking-tight text-vp-background px-2.5 py-0.5 rounded-md bg-vp-chart-4 border border-vp-chart-4 shadow-inner"
      : "text-2xl font-black font-mono tabular-nums tracking-tight text-vp-background px-2.5 py-0.5 rounded-md bg-vp-accent border border-vp-accent shadow-inner";
  distEl.textContent = `${distanceM} m`;
  inner.appendChild(title);
  inner.appendChild(distEl);
  outer.appendChild(inner);
  return outer;
}

function updateObstacleLabelDistance (
  el: HTMLDivElement,
  distanceM: number
): void {
  const distEl = el.querySelector<HTMLElement>("[data-vp-obs-dist]");
  if (distEl) {
    distEl.textContent = `${distanceM} m`;
  }
}

type SpeedBumpProfile = "short" | "long";

function buildSpeedBumpGroup (profile: SpeedBumpProfile): THREE.Group {
  const g = new THREE.Group();
  const roadW = PREVIEW_SCENE_ROAD_WIDTH_M;
  const wScale = roadW / 12;
  const widthX =
    profile === "long"
      ? Math.min(7.5 * wScale, roadW * 0.92)
      : PREVIEW_SCENE_VEHICLE_CHASSIS_DEPTH_M * wScale;
  const stripeCount = profile === "long" ? 6 : 3;
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(widthX, 0.12, 0.6),
    new THREE.MeshStandardMaterial({
      color: "#ffcc00",
      metalness: 0.3,
      roughness: 0.6,
    })
  );
  body.position.set(0, 0.08, 0);
  g.add(body);
  const span = widthX - 0.4;
  const step = stripeCount > 1 ? span / (stripeCount - 1) : 0;
  const startX = -span / 2;
  for (let i = 0; i < stripeCount; i++) {
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.02, 0.65),
      new THREE.MeshStandardMaterial({ color: "#1a1a1a" })
    );
    stripe.position.set(startX + i * step, 0.15, 0);
    g.add(stripe);
  }
  const labelDiv = createObstacleLabelElement("speedBump", 0);
  const label = new CSS2DObject(labelDiv);
  label.position.set(0, OBSTACLE_LABEL_BASE_Y, 0);
  g.add(label);
  g.userData.vpCss2dLabel = label;
  return g;
}

function buildManholeGroup (): THREE.Group {
  const g = new THREE.Group();
  const base = new THREE.Mesh(
    new THREE.CircleGeometry(0.6, 24),
    new THREE.MeshStandardMaterial({
      color: "#2a2a2a",
      metalness: 0.7,
      roughness: 0.4,
    })
  );
  base.rotation.x = -Math.PI / 2;
  base.position.set(0, 0.02, 0);
  g.add(base);
  const ring1 = new THREE.Mesh(
    new THREE.RingGeometry(0.2, 0.55, 24),
    new THREE.MeshStandardMaterial({
      color: "#3a3a3a",
      metalness: 0.8,
      roughness: 0.3,
    })
  );
  ring1.rotation.x = -Math.PI / 2;
  ring1.position.set(0, 0.03, 0);
  g.add(ring1);
  const ring2 = new THREE.Mesh(
    new THREE.RingGeometry(0.58, 0.65, 24),
    new THREE.MeshBasicMaterial({
      color: "#00d4ff",
      transparent: true,
      opacity: 0.5,
    })
  );
  ring2.rotation.x = -Math.PI / 2;
  ring2.position.set(0, 0.04, 0);
  g.add(ring2);
  const labelDiv = createObstacleLabelElement("manhole", 0);
  const label = new CSS2DObject(labelDiv);
  label.position.set(0, OBSTACLE_LABEL_BASE_Y, 0);
  g.add(label);
  g.userData.vpCss2dLabel = label;
  return g;
}

function speedBumpProfileFromLane (
  lane: PreviewObstacleLane | undefined
): SpeedBumpProfile {
  return lane === "center" ? "long" : "short";
}

function obstacleShapeKey (o: ObstacleInfo): string {
  if (o.type === "manhole") {
    return "manhole";
  }
  return `speedBump:${speedBumpProfileFromLane(o.lane)}`;
}

function spawnObstacleGroupForInfo (o: ObstacleInfo): THREE.Group {
  if (o.type === "manhole") {
    return buildManholeGroup();
  }
  return buildSpeedBumpGroup(speedBumpProfileFromLane(o.lane));
}

/**
 * 从场景摘掉障碍物 Group 前必须调用：CSS2D 标签挂在子节点上，父 Group 被 remove 时
 * 子节点不会收到 `removed` 事件，Three 内置的 element.remove() 不会执行，标签会残留在 DOM。
 */
function detachCss2DUnder (root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (obj instanceof CSS2DObject && obj.element.parentNode) {
      obj.element.remove();
    }
  });
}

/**
 * 将 GLB 车模缩放到与原先简模相近的车长（XZ 最大边），底面贴 y=0、居中 XZ；返回用于轮速动画的 Mesh（按命名启发式）。
 */
/** 页面已卸载时丢弃未挂载的 GLB，避免 GPU 泄漏 */
function disposeOrphanGltfRoot (root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    obj.geometry?.dispose();
    const mats = obj.material;
    const list = Array.isArray(mats) ? mats : [mats];
    for (const mat of list) {
      mat?.dispose();
    }
  });
}

/**
 * 老导出 glTF 常缺 colorSpace；与 outputColorSpace=SRGB 搭配时
 * 颜色贴图须 sRGB，法线/ORM 等须按数据纹理处理，否则发灰或法线错。
 */
/** 01pro.glb 常为单材质白模，把 night 车身 BMP 赋给 Standard/Physical 的 map */
function applyX01NightBodyTexture (
  root: THREE.Object3D,
  tex: THREE.Texture
): void {
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.flipY = false;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (const mat of mats) {
      if (
        mat instanceof THREE.MeshStandardMaterial ||
        mat instanceof THREE.MeshPhysicalMaterial
      ) {
        mat.map = tex;
        mat.color.setHex(0xffffff);
        mat.metalness = 0.22;
        mat.roughness = 0.52;
        mat.needsUpdate = true;
      }
    }
  });
}

function applyGltfTextureColorSpaces (root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (const mat of mats) {
      if (
        !(mat instanceof THREE.MeshStandardMaterial) &&
        !(mat instanceof THREE.MeshPhysicalMaterial)
      ) {
        continue;
      }
      const setSrgb = (tex: THREE.Texture | null | undefined): void => {
        if (tex) tex.colorSpace = THREE.SRGBColorSpace;
      };
      const setData = (tex: THREE.Texture | null | undefined): void => {
        if (tex) tex.colorSpace = THREE.NoColorSpace;
      };
      setSrgb(mat.map);
      setSrgb(mat.emissiveMap);
      setData(mat.normalMap);
      setData(mat.roughnessMap);
      setData(mat.metalnessMap);
      setData(mat.aoMap);
      setData(mat.alphaMap);
      setData(mat.bumpMap);
      setData(mat.displacementMap);
      setData(mat.lightMap);
      if (mat instanceof THREE.MeshPhysicalMaterial) {
        setData(mat.clearcoatNormalMap);
        setSrgb(mat.sheenColorMap);
        setSrgb(mat.specularColorMap);
      }
    }
  });
}

function fitGltfVehicleAndCollectWheels (
  root: THREE.Object3D,
  targetLengthM: number
): THREE.Mesh[] {
  const box = new THREE.Box3().setFromObject(root);
  const size0 = box.getSize(new THREE.Vector3());
  if (size0.x > size0.z * 1.15) {
    root.rotation.y = Math.PI / 2;
  }
  // 与简模一致：车头朝世界 +Z；多数 glTF 导出为 -Z 朝前，补 180°
  root.rotateY(Math.PI);
  box.setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const lengthXZ = Math.max(size.x, size.z);
  const s = lengthXZ > 1e-6 ? targetLengthM / lengthXZ : 1;
  root.scale.setScalar(s);
  box.setFromObject(root);
  root.position.set(
    -(box.min.x + box.max.x) / 2,
    -box.min.y,
    -(box.min.z + box.max.z) / 2
  );
  const wheels: THREE.Mesh[] = [];
  root.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      const n = obj.name.toLowerCase();
      if (/(wheel|tire|tyre|rim|轮)/.test(n)) {
        wheels.push(obj);
      }
    }
  });
  return wheels;
}

/** 原程序化车体（GLB 加载失败时回退）；与 `PREVIEW_SCENE_VEHICLE_DISPLAY_SCALE` 对齐 */
function buildProceduralVehicle (vehicleGroup: THREE.Group): THREE.Mesh[] {
  const k = PREVIEW_SCENE_VEHICLE_DISPLAY_SCALE;
  const bodyMat = new THREE.MeshStandardMaterial({
    color: "#1e3a5f",
    metalness: 0.7,
    roughness: 0.3,
  });
  const chassis = new THREE.Mesh(
    new THREE.BoxGeometry(1.8 * k, 0.4 * k, PREVIEW_SCENE_VEHICLE_TARGET_DEPTH_M),
    bodyMat
  );
  chassis.position.set(0, 0.2 * k, 0);
  vehicleGroup.add(chassis);
  const cabin = new THREE.Mesh(
    new THREE.BoxGeometry(1.5 * k, 0.5 * k, 2 * k),
    bodyMat
  );
  cabin.position.set(0, 0.55 * k, -0.2 * k);
  vehicleGroup.add(cabin);

  const glassMat = new THREE.MeshStandardMaterial({
    color: "#00d4ff",
    transparent: true,
    opacity: 0.3,
    emissive: "#00d4ff",
    emissiveIntensity: 0.5,
  });
  const ws = new THREE.Mesh(new THREE.BoxGeometry(1.4 * k, 0.02 * k, 0.6 * k), glassMat);
  ws.position.set(0, 0.55 * k, 0.85 * k);
  ws.rotation.x = Math.PI / 6;
  vehicleGroup.add(ws);
  const rw = new THREE.Mesh(new THREE.BoxGeometry(1.4 * k, 0.02 * k, 0.6 * k), glassMat);
  rw.position.set(0, 0.55 * k, -1.25 * k);
  rw.rotation.x = -Math.PI / 6;
  vehicleGroup.add(rw);

  const headMat = new THREE.MeshStandardMaterial({
    color: "#ffffff",
    emissive: "#ffffff",
    emissiveIntensity: 2,
  });
  const hl1 = new THREE.Mesh(
    new THREE.BoxGeometry(0.3 * k, 0.15 * k, 0.02 * k),
    headMat
  );
  hl1.position.set(0.6 * k, 0.2 * k, 1.76 * k);
  vehicleGroup.add(hl1);
  const hl2 = new THREE.Mesh(
    new THREE.BoxGeometry(0.3 * k, 0.15 * k, 0.02 * k),
    headMat.clone()
  );
  hl2.position.set(-0.6 * k, 0.2 * k, 1.76 * k);
  vehicleGroup.add(hl2);

  const tailMat = new THREE.MeshStandardMaterial({
    color: "#ff3333",
    emissive: "#ff3333",
    emissiveIntensity: 1.5,
  });
  const tl1 = new THREE.Mesh(
    new THREE.BoxGeometry(0.25 * k, 0.1 * k, 0.02 * k),
    tailMat
  );
  tl1.position.set(0.7 * k, 0.2 * k, -1.76 * k);
  vehicleGroup.add(tl1);
  const tl2 = new THREE.Mesh(
    new THREE.BoxGeometry(0.25 * k, 0.1 * k, 0.02 * k),
    tailMat.clone()
  );
  tl2.position.set(-0.7 * k, 0.2 * k, -1.76 * k);
  vehicleGroup.add(tl2);

  const underglow = new THREE.Mesh(
    new THREE.BoxGeometry(1.6 * k, 0.02 * k, 3.3 * k),
    new THREE.MeshStandardMaterial({
      color: "#00d4ff",
      emissive: "#00d4ff",
      emissiveIntensity: 1,
      transparent: true,
      opacity: 0.8,
    })
  );
  underglow.position.set(0, 0.02 * k, 0);
  vehicleGroup.add(underglow);

  const wheelR = 0.25 * k;
  const wheelGeometry = new THREE.CylinderGeometry(wheelR, wheelR, 0.15 * k, 16);
  const wheelMaterial = new THREE.MeshStandardMaterial({
    color: "#1a1a2e",
    metalness: 0.8,
    roughness: 0.3,
  });
  const wheelMeshes: THREE.Mesh[] = [];
  const wx = 0.9 * k;
  const wzF = 1 * k;
  const wzR = -1 * k;
  const wheelPositions: Array<[number, number, number]> = [
    [wx, -0.05 * k, wzF],
    [-wx, -0.05 * k, wzF],
    [wx, -0.05 * k, wzR],
    [-wx, -0.05 * k, wzR],
  ];
  for (let i = 0; i < 4; i++) {
    const w = new THREE.Mesh(wheelGeometry, wheelMaterial);
    w.position.set(...wheelPositions[i]);
    w.rotation.z = Math.PI / 2;
    vehicleGroup.add(w);
    wheelMeshes.push(w);
  }

  const rimMat = new THREE.MeshStandardMaterial({
    color: "#ff6b35",
    emissive: "#ff6b35",
    emissiveIntensity: 0.8,
    metalness: 0.9,
    roughness: 0.1,
  });
  for (let i = 0; i < 4; i++) {
    const rim = new THREE.Mesh(
      new THREE.CylinderGeometry(0.15 * k, 0.15 * k, 0.16 * k, 16),
      rimMat
    );
    rim.position.set(...wheelPositions[i]);
    rim.rotation.z = Math.PI / 2;
    vehicleGroup.add(rim);
  }

  const lidarBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.15 * k, 0.2 * k, 0.15 * k, 8),
    new THREE.MeshStandardMaterial({
      color: "#1a1a2e",
      metalness: 0.9,
      roughness: 0.2,
    })
  );
  lidarBase.position.set(0, 0.95 * k, -0.2 * k);
  vehicleGroup.add(lidarBase);
  const lidarTop = new THREE.Mesh(
    new THREE.CylinderGeometry(0.12 * k, 0.12 * k, 0.05 * k, 16),
    new THREE.MeshStandardMaterial({
      color: "#00d4ff",
      emissive: "#00d4ff",
      emissiveIntensity: 2,
    })
  );
  lidarTop.position.set(0, 1.05 * k, -0.2 * k);
  vehicleGroup.add(lidarTop);

  return wheelMeshes;
}

const dashLineVertexShader = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const dashLineFragmentShader = `
uniform vec3 uColor;
uniform float uScroll;
uniform float uDashLen;
uniform float uGapLen;
uniform float uRoadHalf;
varying vec2 vUv;
void main() {
  float z = mix(-uRoadHalf, uRoadHalf, vUv.y);
  float pos = z + uScroll;
  float period = uDashLen + uGapLen;
  float m = mod(pos, period);
  if (m > uDashLen) discard;
  gl_FragColor = vec4(uColor, 1.0);
}
`;

function createDashLineMesh (
  width: number,
  color: THREE.ColorRepresentation,
  dashLen: number,
  gapLen: number
): { mesh: THREE.Mesh; material: THREE.ShaderMaterial } {
  const geom = new THREE.PlaneGeometry(width, ROAD_LENGTH, 1, 64);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uScroll: { value: 0 },
      uDashLen: { value: dashLen },
      uGapLen: { value: gapLen },
      uRoadHalf: { value: ROAD_HALF },
    },
    vertexShader: dashLineVertexShader,
    fragmentShader: dashLineFragmentShader,
  });
  const mesh = new THREE.Mesh(geom, material);
  mesh.rotation.x = -Math.PI / 2;
  return { mesh, material };
}

export function createVehiclePerceptionScene (
  options: CreateVehiclePerceptionSceneOptions
): { dispose: () => void; resize: () => void; resetView: () => void } {
  const {
    container,
    getVehicleSpeedKmh,
    getObstacles,
    getPreviewDisplayRangeM,
  } = options;

  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog(FOG_COLOR, 40, 100);
  scene.background = new THREE.Color(FOG_COLOR);

  const camera = new THREE.PerspectiveCamera(
    60,
    container.clientWidth / Math.max(container.clientHeight, 1),
    0.1,
    500
  );
  camera.position.set(0, 6, 12);

  const webglRenderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: "high-performance",
  });
  /** 强制 1 会在高 DPI 上明显锯齿；上限 2 平衡清晰度与车机 GPU 负载 */
  const rendererPixelRatio = Math.min(window.devicePixelRatio || 1, 2);
  webglRenderer.setPixelRatio(rendererPixelRatio);
  webglRenderer.setSize(container.clientWidth, container.clientHeight);
  webglRenderer.toneMapping = THREE.ACESFilmicToneMapping;
  webglRenderer.toneMappingExposure = 1.22;
  webglRenderer.outputColorSpace = THREE.SRGBColorSpace;
  webglRenderer.domElement.style.display = "block";
  webglRenderer.domElement.style.width = "100%";
  webglRenderer.domElement.style.height = "100%";

  const css2DRenderer = new CSS2DRenderer();
  css2DRenderer.setSize(container.clientWidth, container.clientHeight);
  css2DRenderer.domElement.style.position = "absolute";
  css2DRenderer.domElement.style.inset = "0";
  css2DRenderer.domElement.style.pointerEvents = "none";

  container.style.position = "relative";
  container.appendChild(webglRenderer.domElement);
  container.appendChild(css2DRenderer.domElement);

  const controls = new OrbitControls(camera, webglRenderer.domElement);
  controls.enablePan = false;
  controls.enableZoom = true;
  controls.minPolarAngle = Math.PI / 6;
  controls.maxPolarAngle = Math.PI / 2.5;
  controls.minDistance = 4;
  controls.maxDistance = 50;
  controls.target.set(0, 1, 0);

  const standardTarget = new THREE.Vector3().copy(controls.target);
  const standardOffsetDir = new THREE.Vector3()
    .subVectors(camera.position, controls.target)
    .normalize();

  /** 与当前设置量程对应的标称距离；resetView 与量程变更时更新 */
  let standardOrbitDistance = 8.5;

  function clampOrbitDistance (d: number): number {
    return Math.min(controls.maxDistance, Math.max(controls.minDistance, d));
  }

  /** 量程变化时沿当前视角方向缩放到新距离，不强行改方位角 */
  function applyOrbitDistanceFromRangeM (rangeM: number): void {
    standardOrbitDistance = orbitDistanceForRangeM(rangeM);
    const d = clampOrbitDistance(standardOrbitDistance);
    const t = controls.target;
    const offset = new THREE.Vector3().subVectors(camera.position, t);
    if (offset.lengthSq() < 1e-8) {
      offset.copy(standardOffsetDir).multiplyScalar(d);
    } else {
      offset.normalize().multiplyScalar(d);
    }
    camera.position.copy(t).add(offset);
    controls.update();
  }

  function applyFogForRangeM (rangeM: number): void {
    const r = clampPreviewRangeM(rangeM);
    const fog = scene.fog;
    if (fog instanceof THREE.Fog) {
      fog.near = 8 + r * 0.38;
      fog.far = Math.min(165, 26 + r * 3.15);
    }
  }

  let lastFogRangeM = Number.NaN;

  function applyStandardCameraFrame (): void {
    controls.target.copy(standardTarget);
    const d = clampOrbitDistance(standardOrbitDistance);
    camera.position.copy(
      standardTarget
        .clone()
        .add(standardOffsetDir.clone().multiplyScalar(d))
    );
    controls.update();
  }

  function resetView (): void {
    applyStandardCameraFrame();
  }

  let lastOrbitRangeM = Number.NaN;
  {
    const r0 = clampPreviewRangeM(getPreviewDisplayRangeM());
    standardOrbitDistance = orbitDistanceForRangeM(r0);
    applyStandardCameraFrame();
    applyFogForRangeM(r0);
    lastFogRangeM = r0;
    lastOrbitRangeM = r0;
  }

  scene.add(new THREE.AmbientLight(0xffffff, 0.68));
  const d1 = new THREE.DirectionalLight(0xffffff, 1.08);
  d1.position.set(5, 10, 5);
  scene.add(d1);
  const d2 = new THREE.DirectionalLight(0xffffff, 0.58);
  d2.position.set(-5, 8, -5);
  scene.add(d2);
  const p1 = new THREE.PointLight(0x00d4ff, 1, 30);
  p1.position.set(0, 8, 0);
  scene.add(p1);
  const p2 = new THREE.PointLight(0xff6b35, 0.6, 20);
  p2.position.set(0, 3, 8);
  scene.add(p2);

  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(200, 200),
    new THREE.MeshStandardMaterial({
      color: FOG_COLOR,
      metalness: 0.2,
      roughness: 0.8,
    })
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -0.01;
  scene.add(ground);

  const roadGroup = new THREE.Group();
  scene.add(roadGroup);

  const roadSurface = new THREE.Mesh(
    new THREE.PlaneGeometry(PREVIEW_SCENE_ROAD_WIDTH_M, ROAD_LENGTH),
    new THREE.MeshStandardMaterial({
      color: "#1a1a2a",
      metalness: 0.2,
      roughness: 0.7,
    })
  );
  roadSurface.rotation.x = -Math.PI / 2;
  roadSurface.position.y = 0.01;
  roadGroup.add(roadSurface);

  const edgeMat = new THREE.MeshStandardMaterial({
    color: "#00d4ff",
    emissive: "#00d4ff",
    emissiveIntensity: 1.5,
  });
  const roadHalfW = PREVIEW_SCENE_ROAD_HALF_WIDTH_M;
  const leftEdge = new THREE.Mesh(
    new THREE.BoxGeometry(0.15, 0.08, ROAD_LENGTH),
    edgeMat
  );
  leftEdge.position.set(-roadHalfW, 0.05, 0);
  roadGroup.add(leftEdge);
  const rightEdge = new THREE.Mesh(
    new THREE.BoxGeometry(0.15, 0.08, ROAD_LENGTH),
    edgeMat.clone()
  );
  rightEdge.position.set(roadHalfW, 0.05, 0);
  roadGroup.add(rightEdge);

  /* 中间车道虚线（暂不展示，恢复时取消注释并同步 tick 内 uScroll）
  const { mesh: centerDash, material: centerDashMat } = createDashLineMesh(
    0.22,
    0xffffff,
    2,
    2
  );
  centerDash.position.set(0, 0.035, 0);
  roadGroup.add(centerDash);
  */

  const laneDashX = PREVIEW_SCENE_ROAD_WIDTH_M / 4;

  const { mesh: sideDashL, material: sideDashLMat } = createDashLineMesh(
    0.08,
    0x3a3a5a,
    3,
    3
  );
  sideDashL.position.set(-laneDashX, 0.025, 0);
  roadGroup.add(sideDashL);

  const { mesh: sideDashR, material: sideDashRMat } = createDashLineMesh(
    0.08,
    0x3a3a5a,
    3,
    3
  );
  sideDashR.position.set(laneDashX, 0.025, 0);
  roadGroup.add(sideDashR);

  const gridMat = new THREE.MeshStandardMaterial({
    color: "#1a1a3a",
    emissive: "#00d4ff",
    emissiveIntensity: 0.2,
    transparent: true,
    opacity: 0.5,
  });
  const gridMeshes: THREE.Mesh[] = [];
  const gridEdgeX = 8 * (roadHalfW / 6);
  const gridBoxW = 4 * (roadHalfW / 6);
  for (let i = 0; i < GRID_COUNT; i++) {
    const z = -ROAD_HALF + i * GRID_SPACING;
    const g1 = new THREE.Mesh(
      new THREE.BoxGeometry(gridBoxW, 0.01, 0.05),
      gridMat
    );
    g1.position.set(-gridEdgeX, 0.01, z);
    roadGroup.add(g1);
    gridMeshes.push(g1);
    const g2 = new THREE.Mesh(
      new THREE.BoxGeometry(gridBoxW, 0.01, 0.05),
      gridMat.clone()
    );
    g2.position.set(gridEdgeX, 0.01, z);
    roadGroup.add(g2);
    gridMeshes.push(g2);
  }

  const vehicleGroup = new THREE.Group();
  vehicleGroup.position.set(0, 0.4, PREVIEW_SCENE_VEHICLE_GROUP_POSITION_Z_M);
  scene.add(vehicleGroup);

  /** 仅略补车顶轮廓，低强度避免与 GLB 材质叠加后过曝发白 */
  const vehicleSoftFill = new THREE.PointLight(0xeef2ff, 0.55, 14);
  vehicleSoftFill.position.set(0.6, 2.2, 1.2);
  vehicleGroup.add(vehicleSoftFill);

  let wheelMeshes: THREE.Mesh[] = [];
  let vehicleAssetDisposed = false;

  function mountPreviewVehicleRoot (root: THREE.Object3D): void {
    if (vehicleAssetDisposed) return;
    applyGltfTextureColorSpaces(root);
    wheelMeshes = fitGltfVehicleAndCollectWheels(
      root,
      PREVIEW_SCENE_VEHICLE_TARGET_DEPTH_M
    );
    vehicleGroup.add(root);
  }

  const gltfLoader = new GLTFLoader();
  const textureLoader = new THREE.TextureLoader();
  gltfLoader.load(
    previewVehicleGlbUrl,
    (gltf) => {
      if (vehicleAssetDisposed) {
        disposeOrphanGltfRoot(gltf.scene);
        return;
      }
      const root = gltf.scene;
      root.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.castShadow = true;
          obj.receiveShadow = true;
        }
      });
      textureLoader.load(
        x01NightBodyBmpUrl,
        (tex) => {
          if (vehicleAssetDisposed) {
            tex.dispose();
            return;
          }
          applyX01NightBodyTexture(root, tex);
          mountPreviewVehicleRoot(root);
        },
        undefined,
        (texErr) => {
          console.warn(
            "[vehiclePerception] 01自车night 车漆贴图加载失败，保持白模",
            texErr
          );
          if (!vehicleAssetDisposed) {
            mountPreviewVehicleRoot(root);
          }
        }
      );
    },
    undefined,
    (err) => {
      console.warn(
        "[vehiclePerception] GLB 车模加载失败，使用程序化简模",
        err
      );
      if (vehicleAssetDisposed) {
        return;
      }
      wheelMeshes = buildProceduralVehicle(vehicleGroup);
    }
  );

  const obstaclesGroup = new THREE.Group();
  scene.add(obstaclesGroup);

  const obstaclePool: THREE.Group[] = [];

  function syncObstacleGroups (list: ObstacleInfo[]): void {
    for (let i = 0; i < list.length; i++) {
      let grp = obstaclePool[i];
      const needKey = obstacleShapeKey(list[i]);
      if (!grp) {
        grp = spawnObstacleGroupForInfo(list[i]);
        obstaclePool.push(grp);
        obstaclesGroup.add(grp);
      }
      const curKey = grp.userData.vpShapeKey as string | undefined;
      if (curKey != null && curKey !== needKey) {
        detachCss2DUnder(grp);
        obstaclesGroup.remove(grp);
        grp = spawnObstacleGroupForInfo(list[i]);
        obstaclePool[i] = grp;
        obstaclesGroup.add(grp);
      }
      grp.userData.vpShapeKey = needKey;
      grp.userData.vpType = list[i].type;
      grp.visible = true;
      grp.position.set(list[i].x, 0.02, list[i].z);
      const labelObj = grp.userData.vpCss2dLabel as CSS2DObject | undefined;
      if (labelObj?.element instanceof HTMLDivElement) {
        updateObstacleLabelDistance(labelObj.element, list[i].distance);
      }
    }
    for (let j = list.length; j < obstaclePool.length; j++) {
      obstaclePool[j].visible = false;
    }
  }

  /** 按相机距离更新标签 CSS scale 与局部 Y（CSS2D 外层 transform 仅平移，缩放只能写在内层） */
  function syncObstacleLabelsDepth (obstacleCount: number): void {
    camera.getWorldPosition(_vpCamWorld);
    for (let i = 0; i < obstacleCount; i++) {
      const grp = obstaclePool[i];
      if (!grp?.visible) continue;
      const labelObj = grp.userData.vpCss2dLabel as CSS2DObject | undefined;
      const el = labelObj?.element;
      if (!el) continue;
      const inner = el.querySelector<HTMLElement>("[data-vp-obs-label-inner]");
      if (!inner) continue;

      labelObj.getWorldPosition(_vpLabelWorld);
      const camDist = Math.max(0.25, _vpLabelWorld.distanceTo(_vpCamWorld));
      const scale = THREE.MathUtils.clamp(
        OBSTACLE_LABEL_DEPTH_REF_DIST_M / camDist,
        OBSTACLE_LABEL_SCALE_MIN,
        OBSTACLE_LABEL_SCALE_MAX
      );
      inner.style.transformOrigin = "50% 100%";
      inner.style.transform = `scale(${scale})`;

      const yBoost = THREE.MathUtils.clamp(
        (camDist - OBSTACLE_LABEL_Y_BOOST_START_CAM_DIST_M) *
          OBSTACLE_LABEL_Y_BOOST_PER_M,
        0,
        OBSTACLE_LABEL_Y_BOOST_MAX
      );
      labelObj.position.y = OBSTACLE_LABEL_BASE_Y + yBoost;
    }
  }

  /* 自车下方地面扫描圆环装饰（暂不展示）
  const scanGroup = new THREE.Group();
  scanGroup.position.set(0, 0.1, 0);
  scene.add(scanGroup);

  const scanRing = new THREE.Mesh(
    new THREE.RingGeometry(7.5, 8, 64, 1, 0, Math.PI / 2),
    new THREE.MeshBasicMaterial({
      color: "#00d4ff",
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
    })
  );
  scanRing.rotation.x = -Math.PI / 2;
  scanGroup.add(scanRing);

  for (const radius of [3, 5, 7]) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(radius - 0.03, radius + 0.03, 48),
      new THREE.MeshBasicMaterial({
        color: "#00d4ff",
        transparent: true,
        opacity: 0.15,
        side: THREE.DoubleSide,
      })
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.02;
    scanGroup.add(ring);
  }

  for (const angle of [0, Math.PI / 4, Math.PI / 2, Math.PI * 0.75]) {
    const pl = new THREE.Mesh(
      new THREE.PlaneGeometry(0.03, 16),
      new THREE.MeshBasicMaterial({
        color: "#00d4ff",
        transparent: true,
        opacity: 0.1,
        side: THREE.DoubleSide,
      })
    );
    pl.position.y = 0.02;
    pl.rotation.x = -Math.PI / 2;
    pl.rotation.z = angle;
    scanGroup.add(pl);
  }
  */

  const clock = new THREE.Clock();
  let rafId = 0;
  let disposed = false;
  let lineScrollAccum = 0;

  const tick = (): void => {
    if (disposed) return;
    rafId = requestAnimationFrame(tick);
    const delta = clock.getDelta();
    const rangeM = clampPreviewRangeM(getPreviewDisplayRangeM());
    if (rangeM !== lastFogRangeM) {
      applyFogForRangeM(rangeM);
      lastFogRangeM = rangeM;
    }
    if (rangeM !== lastOrbitRangeM) {
      applyOrbitDistanceFromRangeM(rangeM);
      lastOrbitRangeM = rangeM;
    }
    controls.update();

    const speedKmh = Math.max(0, getVehicleSpeedKmh() ?? 0);
    const speedMps = speedKmh / 3.6;

    lineScrollAccum += speedMps * delta;
    // centerDashMat.uniforms.uScroll.value = lineScrollAccum;
    sideDashLMat.uniforms.uScroll.value = lineScrollAccum;
    sideDashRMat.uniforms.uScroll.value = lineScrollAccum;

    for (const m of gridMeshes) {
      m.position.z += speedMps * delta;
      while (m.position.z > ROAD_HALF + GRID_SPACING) {
        m.position.z -= Z_WRAP;
      }
    }

    const obs = getObstacles();
    syncObstacleGroups(obs);
    syncObstacleLabelsDepth(obs.length);

    const wheelSpin = speedMps * delta * 4;
    wheelMeshes.forEach((wheel) => {
      wheel.rotation.x += wheelSpin;
    });

    // scanRing.rotation.z = clock.elapsedTime * 1.5;

    css2DRenderer.render(scene, camera);
    webglRenderer.render(scene, camera);
  };

  tick();

  const resize = (): void => {
    const w = container.clientWidth;
    const h = Math.max(container.clientHeight, 1);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    webglRenderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    webglRenderer.setSize(w, h);
    css2DRenderer.setSize(w, h);
  };

  const dispose = (): void => {
    disposed = true;
    vehicleAssetDisposed = true;
    cancelAnimationFrame(rafId);
    controls.dispose();
    webglRenderer.dispose();
    if (webglRenderer.domElement.parentNode === container) {
      container.removeChild(webglRenderer.domElement);
    }
    if (css2DRenderer.domElement.parentNode === container) {
      container.removeChild(css2DRenderer.domElement);
    }
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        if (obj.geometry) {
          geometries.add(obj.geometry);
        }
        const m = obj.material;
        if (Array.isArray(m)) {
          m.forEach((x) => materials.add(x));
        } else if (m) {
          materials.add(m);
        }
      }
    });
    geometries.forEach((g) => {
      g.dispose();
    });
    materials.forEach((m) => {
      m.dispose();
    });
  };

  return { dispose, resize, resetView };
}
